import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types"; // Add this import
import { FaSearch, FaTimes } from "react-icons/fa";
import { GiExplosiveMaterials } from "react-icons/gi";
import { FaSort } from "react-icons/fa";
import { CiFilter } from "react-icons/ci";

import MaterialButton from "./DropdownButton";
import Tag from "./Tag";
import DateRangePicker from "./DateRangePicker";
import ResetButton from "../Helpers/ResetButton";
import ExportButton from "./ExportButton";

const FilterSearchBar = ({
  categoryFilter,
  setCategoryFilter,
  ascending,
  setAscending,
  filterOptions,
  setFilterOptions,
  dropdownItems,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  searchPlaceholder = "Search here...",
  searchQuery,
  onSearchChange,
  resetFunction,
  exportToExcel
}) => {
  // Memoized handler to prevent unnecessary re-renders
  const handleDropdownSelect = useCallback(
    (option) => {
      setCategoryFilter((prevFilters) =>
        prevFilters.includes(option)
          ? prevFilters.filter((item) => item !== option)
          : [...prevFilters, option]
      );
    },
    [setCategoryFilter]
  );

  const handleSortClick = useCallback(
    (option) => {
      setAscending(option === "Ascending" ? true : false);
    },
    [setAscending]
  );

  const handleFilterClick = useCallback(
    (option) => {
      setFilterOptions((prevOptions) =>
        prevOptions.includes(option)
          ? prevOptions.filter((item) => item !== option)
          : [...prevOptions, option]
      );
    },
    [setFilterOptions]
  );

  const handleRemoveFilter = useCallback(
    (item, filterType) => {
      switch (filterType) {
        case "categoryFilter":
          setCategoryFilter((prevFilters) =>
            prevFilters.filter((f) => f !== item)
          );
          break;
        case "ascending":
          setAscending(null);
          break;
        case "filterOptions":
          setFilterOptions((prevOptions) =>
            prevOptions.filter((f) => f !== item)
          );
          break;
        default:
          break;
      }
    },
    [setCategoryFilter, setFilterOptions, setAscending]
  );

  const clearSearch = useCallback(() => {
    onSearchChange("");
  }, [onSearchChange]);

  // Determine active sort selection for the sort button
  const activeSortSelections =
    ascending === null ? [] : [ascending ? "Ascending" : "Descending"];

  // Render active filters section only if there are active filters
  const hasActiveFilters =
    categoryFilter.length > 0 || ascending !== null || filterOptions.length > 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 20,
        }}
      >
        {/* Filter Buttons Section */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <MaterialButton
            name="Categories"
            options={dropdownItems}
            onSubmit={handleDropdownSelect}
            icon={<GiExplosiveMaterials color="#696969" size={15} />}
            activeSelections={categoryFilter}
          />
          <MaterialButton
            name="Sort By"
            options={["Ascending", "Descending"]}
            onSubmit={handleSortClick}
            icon={<FaSort color="#696969" size={15} />}
            activeSelections={activeSortSelections}
          />
          <MaterialButton
            name="Filters"
            options={[
              "Pending Items",
              "Approved Items",
              "Declined Items",
              "PO Released",
              "Material Reached",
            ]}
            onSubmit={handleFilterClick}
            icon={<CiFilter color="#696969" size={15} />}
            activeSelections={filterOptions}
          />
        </div>

        {/* Date Range Picker */}
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />

        {/* Search Input */}
        <div style={{ position: "relative", display: "inline-block" }}>
          <input
            type="text"
            aria-label="Search"
            style={{
              height: 50,
              width: 400,
              paddingLeft: 40,
              paddingRight: 40,
              borderRadius: 25,
              border: "1px solid #ddd",
              backgroundColor: "#f9f9f9",
              fontSize: 16,
              color: "#333",
              outline: "none",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
            }}
            value={searchQuery}
            placeholder={searchPlaceholder}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <FaSearch
            style={{
              position: "absolute",
              top: "50%",
              left: 12,
              transform: "translateY(-50%)",
              color: "#888",
              fontSize: 20,
              pointerEvents: "none",
            }}
          />
          {searchQuery && (
            <FaTimes
              style={{
                position: "absolute",
                top: "50%",
                right: 12,
                transform: "translateY(-50%)",
                color: "#888",
                fontSize: 20,
                cursor: "pointer",
              }}
              onClick={clearSearch}
              aria-label="Clear search"
            />
          )}
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 20,
          }}
        >
          <ResetButton resetFunction={resetFunction} />
          <ExportButton exportToExcel={exportToExcel} />
        </div>
      </div>

      {/* Active Filters Tags */}
      {hasActiveFilters && (
        <div>
          <p
            style={{
              fontWeight: "500",
              marginTop: 5,
              marginBottom: 5,
              color: "#353535",
            }}
          >
            Active Filters
          </p>
          {categoryFilter.map((item) => (
            <Tag
              key={item}
              label={item}
              onRemove={() => handleRemoveFilter(item, "categoryFilter")}
            />
          ))}

          {ascending !== null && (
            <Tag
              key="sort-order"
              label={ascending ? "Ascending" : "Descending"}
              onRemove={() => handleRemoveFilter(null, "ascending")}
            />
          )}

          {filterOptions.map((item) => (
            <Tag
              key={item}
              label={item}
              onRemove={() => handleRemoveFilter(item, "filterOptions")}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// PropTypes for runtime type checking
FilterSearchBar.propTypes = {
  categoryFilter: PropTypes.arrayOf(PropTypes.string),
  setCategoryFilter: PropTypes.func.isRequired,
  ascending: PropTypes.bool,
  setAscending: PropTypes.func.isRequired,
  filterOptions: PropTypes.arrayOf(PropTypes.string),
  setFilterOptions: PropTypes.func.isRequired,
  dropdownItems: PropTypes.arrayOf(PropTypes.string),
  startDate: PropTypes.instanceOf(Date),
  setStartDate: PropTypes.func.isRequired,
  endDate: PropTypes.instanceOf(Date),
  setEndDate: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  resetFunction: PropTypes.func.isRequired,
};

export default FilterSearchBar;
